/**
 * Store 2.0 universal search helpers.
 *
 * `components/Refresh/` holds the redesigned storefront / marketing UI (Store
 * 2.0 initiative) — parallel to legacy `components/Store/` until flags are removed.
 *
 * Search results include `category` from `SearchResultSerializer` (`sku_category.name`:
 * visit, specialty, condition, medication, lab, imaging, medication_fill,
 * medication_option).
 *
 * `/api/external/search/index` also accepts a `tags` query param for server-side
 * filtering. Category chips here filter on the authoritative `category` field until
 * backend exposes category/tags params aligned with these chips (needed for category-page search).
 */
import { getLowestSearchPriceCents } from '@/components/Store/StoreUtilities'
import {
    STORE_DISPLAY_PRICE_SUBTEXT,
    formatStoreDisplayFromPriceFromCents,
} from '@/lib/store/storeDisplayPricing'
import type { ServiceItem } from '@/types/interfaces/service-item'

/** @deprecated Use STORE_DISPLAY_PRICE_SUBTEXT from `@/lib/store/storeDisplayPricing`. */
export const SEARCH_RESULT_PRICE_SUBTEXT = STORE_DISPLAY_PRICE_SUBTEXT

/**
 * Lowest available price for search result cards, prefixed with "from" (Figma Store 2.0).
 * `primaryPrice` elsewhere means the preferred display price and may follow backend
 * `primary` flags rather than always being the minimum.
 */
export function getSearchResultDisplayPrice(item: ServiceItem): string {
    const cents = getLowestSearchPriceCents(item.price)

    return cents == null ? '' : formatStoreDisplayFromPriceFromCents(cents)
}

/** Search results filter chips on the Store 2.0 search results page (SKU categories plus providers and pharmacies). */
export type SearchResultFilterKey =
    | 'visits'
    | 'medications'
    | 'labs'
    | 'providers'
    | 'pharmacies'

/** Chip labels and sort order for the search results filter row. */
export const SEARCH_RESULT_FILTER_OPTIONS: readonly {
    key: SearchResultFilterKey
    label: string
}[] = [
    { key: 'visits', label: 'Visits' },
    { key: 'medications', label: 'Medications' },
    { key: 'labs', label: 'Labs' },
    { key: 'providers', label: 'Providers' },
    { key: 'pharmacies', label: 'Pharmacies' },
]

function normalizeCategory(category: ServiceItem['category'] | string | undefined): string {
    return String(category ?? '').toLowerCase()
}

/**
 * Lab/med product shots are tightly cropped with little inherent padding, so store
 * cards must render them inset with object-fit: contain (cream well) rather than
 * full-bleed cover. Includes medication_option / medication_fill — the categories
 * home-page shelves and search actually return after the drug-catalog migration.
 */
export function isMedicationSkuCategory(category: ServiceItem['category'] | string | undefined): boolean {
    switch (normalizeCategory(category)) {
        case 'medication':
        case 'medication_fill':
        case 'medication_option':
            return true
        default:
            return false
    }
}

export function usesInsetProductImage(category: ServiceItem['category'] | string | undefined): boolean {
    return normalizeCategory(category) === 'lab' || isMedicationSkuCategory(category)
}

/** Maps a search API result to a filter chip using backend `category` only. */
export function getSearchResultFilterKey(item: ServiceItem): SearchResultFilterKey | null {
    switch (normalizeCategory(item.category)) {
        case 'lab':
            return 'labs'
        case 'medication':
        case 'medication_fill':
        case 'medication_option':
            return 'medications'
        case 'visit':
        case 'condition':
        case 'specialty':
            return 'visits'
        default:
            return null
    }
}

/** User-facing type tag overlaid on each search result image (Figma Store 2.0). Omits tag when unknown. */
export function getSearchResultTypeLabel(item: ServiceItem): string {
    switch (normalizeCategory(item.category)) {
        case 'lab':
            return 'Lab test'
        case 'medication':
        case 'medication_fill':
        case 'medication_option':
            return 'Medication'
        case 'specialty':
        case 'visit':
        case 'condition':
            return 'Visit'
        default:
            return ''
    }
}

/** Client-side category filter on API `category` until search supports chip params server-side. */
export function filterSearchResultsByCategory(
    results: ServiceItem[],
    filter: SearchResultFilterKey | null,
): ServiceItem[] {
    if (!filter) return results
    if (filter === 'providers' || filter === 'pharmacies') return []

    return results.filter((item) => getSearchResultFilterKey(item) === filter)
}

/** SKU filter keys used to pick which search-results promo carousel(s) to show. */
export type SearchResultsCarouselCategoryKey = 'visits' | 'medications' | 'labs'

export interface SearchResultsCarouselVisibility {
    showProvidersCarousel: boolean
    showPharmacyCarousel: boolean
}

const TOP_FIVE_SKU_SAMPLE_SIZE = 5

/**
 * Most common SKU category among the first five search results.
 * Ties break toward the category that appears earliest in that sample.
 * Unknown categories (imaging, empty) are ignored.
 */
export function getTopFiveSkuCategory(
    results: ServiceItem[],
): SearchResultsCarouselCategoryKey | null {
    const sample = results.slice(0, TOP_FIVE_SKU_SAMPLE_SIZE)
    const counts: Record<SearchResultsCarouselCategoryKey, number> = {
        visits: 0,
        medications: 0,
        labs: 0,
    }
    const firstIndex: Partial<Record<SearchResultsCarouselCategoryKey, number>> = {}

    sample.forEach((item, index) => {
        const filterKey = getSearchResultFilterKey(item)
        if (filterKey !== 'visits' && filterKey !== 'medications' && filterKey !== 'labs') {
            return
        }
        counts[filterKey] += 1
        if (firstIndex[filterKey] === undefined) {
            firstIndex[filterKey] = index
        }
    })

    let dominant: SearchResultsCarouselCategoryKey | null = null
    for (const key of ['visits', 'medications', 'labs'] as const) {
        if (counts[key] === 0) continue
        if (
            dominant === null ||
            counts[key] > counts[dominant] ||
            (counts[key] === counts[dominant] &&
                (firstIndex[key] ?? Number.POSITIVE_INFINITY) <
                    (firstIndex[dominant] ?? Number.POSITIVE_INFINITY))
        ) {
            dominant = key
        }
    }

    return dominant
}

/**
 * Provider / pharmacy promo carousel visibility from the dominant top-5 SKU category.
 * Category filter tabs (including Providers / Pharmacies) stay available regardless.
 */
export function getSearchResultsCarouselVisibility(
    results: ServiceItem[],
): SearchResultsCarouselVisibility {
    switch (getTopFiveSkuCategory(results)) {
        case 'visits':
            return { showProvidersCarousel: true, showPharmacyCarousel: false }
        case 'medications':
            return { showProvidersCarousel: false, showPharmacyCarousel: true }
        case 'labs':
        default:
            return { showProvidersCarousel: false, showPharmacyCarousel: false }
    }
}
